import React from 'react'
import { Space, Button, Drawer, Typography, Tooltip, Tag } from 'antd'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Files,
  LogIn,
  LogOut,
  Menu,
  PencilRuler,
  Settings,
  HelpCircle,
  Clock
} from 'lucide-react'
import { UserPopover } from './UserPopover'
import {
  useCompanyStore,
  usePermissions
} from '../../store/company/companyStore'
import { useApi } from '../../services/ApiContext'
import { useTrialStatus } from '../../hooks/useTrialStatus'
import { UpgradeButton } from '../Pricing/UpgradeButton'
const Text = Typography.Text

export const LeftAppNav: React.FC = () => {
  const location = useLocation()

  return (
    <Space className="ml-16 hidden md:flex">
      <Link className="flex" to="/app/documents">
        <Button
          className="font-bold"
          type="text"
          style={{
            [location.pathname.includes('/app/documents')
              ? 'background'
              : 'null']: '#20007f10'
          }}
        >
          FF&E Schedules
        </Button>
      </Link>

      <Link className="flex" to="/app/product-library">
        <Button
          className="font-bold"
          type="text"
          style={{
            [location.pathname.includes('/app/product-library')
              ? 'background'
              : 'null']: '#20007f10'
          }}
        >
          Product Library
        </Button>
      </Link>
      <Link className="flex" to="/app/suppliers">
        <Button
          className="font-bold"
          type="text"
          style={{
            [location.pathname.includes('/app/suppliers')
              ? 'background'
              : 'null']: '#20007f10'
          }}
        >
          Suppliers
        </Button>
      </Link>
      {/* <Link className="flex" to="/app">
        <Button className="font-bold" type="text" disabled>
          Clients
        </Button>
      </Link> */}
    </Space>
  )
}

export const TrialBanner: React.FC = () => {
  const { isTrialActive, isTrialExpired, daysRemaining } = useTrialStatus()
  const { company, user } = useCompanyStore()
  const navigate = useNavigate()

  if (!isTrialActive && !isTrialExpired) return null

  const urgencyColor = daysRemaining <= 3 ? 'red' : daysRemaining <= 7 ? 'orange' : 'blue'

  return (
    <div className="flex items-center gap-2">
      <Tag
        color={urgencyColor}
        className="flex items-center gap-1 px-3 py-1 m-0 rounded-full cursor-pointer"
        onClick={() => navigate('/app/upgrade')}
      >
        <Clock className="h-3 w-3" />
        <span className="font-semibold text-xs">
          {isTrialExpired
            ? 'Trial ended'
            : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`}
        </span>
      </Tag>
      <UpgradeButton company={company} user={user} />
    </div>
  )
}

export const RightAppNav: React.FC = () => {
  const location = useLocation()
  const { hasPermissions } = usePermissions()

  const handleHelpClick = () => {
    // Open Intercom chat
    if (window.Intercom) {
      window.Intercom('show')
    }
  }

  return (
    <Space className="ml-16">
      <div className="flex md:hidden">
        <MobileAppNav />
      </div>
      <div className="hidden md:flex items-center gap-3">
        <Tooltip title="Get help and support" placement="bottom">
          <Button
            className="font-bold"
            type="text"
            icon={<HelpCircle className="h-4 w-4" />}
            onClick={handleHelpClick}
          />
        </Tooltip>
        {hasPermissions(['organisationManagement']) && (
          <Tooltip title="Settings and preferences" placement="bottom">
            <Link className="flex" to="/app/settings">
              <Button
                className="font-bold"
                type="text"
                color={
                  location.pathname === '/app/settings' ? 'primary' : 'default'
                }
                icon={<Settings className="h-4 w-4" />}
              />
            </Link>
          </Tooltip>
        )}
        <UserPopover />
      </div>
    </Space>
  )
}

export const MobileNavButton = ({
  to,
  icon,
  children
}: {
  to: string
  icon: React.ReactNode
  children: React.ReactNode
}) => {
  const location = useLocation()

  return (
    <Link className="flex" to={to}>
      <Button
        className="font-bold w-full flex justify-start"
        type="text"
        style={{
          [location.pathname.includes(to) ? 'background' : 'null']: '#20007f10'
        }}
        icon={icon}
      >
        {children}
      </Button>
    </Link>
  )
}

export const MobileAppNav: React.FC = () => {
  const [open, setOpen] = React.useState(false)
  const { company, user } = useCompanyStore()
  const { logout } = useApi()
  const navigate = useNavigate()
  const { hasPermissions } = usePermissions()

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleHelpClick = () => {
    // Open Intercom chat
    if (window.Intercom) {
      window.Intercom('show')
    }
    setOpen(false) // Close the drawer after clicking help
  }

  return (
    <>
      <Button
        type="default"
        className="font-bold"
        icon={<Menu className="h-4 w-4" />}
        onClick={toggleDrawer}
      />
      <Drawer
        title="SprintSpec"
        placement="right"
        onClose={toggleDrawer}
        open={open}
      >
        <Space className="w-full flex gap-3" direction="vertical">
          <MobileNavButton
            to="/app/editor"
            icon={<PencilRuler className="h-4 w-4" />}
          >
            FF&E Editor
          </MobileNavButton>
          <MobileNavButton
            to="/app/documents"
            icon={<Files className="h-4 w-4" />}
          >
            Schedules
          </MobileNavButton>
          <Button
            type="text"
            className="font-bold w-full flex justify-start"
            icon={<HelpCircle className="h-4 w-4" />}
            onClick={handleHelpClick}
          >
            Get Help
          </Button>
          {hasPermissions(['organisationManagement']) && (
            <MobileNavButton
              to="/app/settings"
              icon={<Settings className="h-4 w-4" />}
            >
              Settings
            </MobileNavButton>
          )}

          {user?.isDemo && (
            <Link to="/login">
              <Button
                type="text"
                className="font-bold w-full flex justify-start"
                icon={<LogIn className="h-4 w-4" />}
              >
                Sign In
              </Button>
            </Link>
          )}
          {user?.isDemo && (
            <Link to="/signup">
              <Button
                type="primary"
                size="large"
                className="font-bold w-full flex justify-start"
                icon={<ArrowRight className="h-4 w-4" />}
              >
                Sign up
              </Button>
            </Link>
          )}

          {user && !user?.isDemo && (
            <div className="px-4 py-3 border-t mt-4 pb-0">
              <Text strong className="block">
                {user.firstName} {user.lastName}
              </Text>
              <Text type="secondary" className="block">
                {user.email}
              </Text>
              <Text className="block mt-1">{company.name}</Text>
            </div>
          )}
          {user && !user.isDemo && (
            <Button
              type="text"
              className="mt-2 w-full flex justify-start"
              icon={<LogOut className="h-4 w-4" />}
              onClick={handleLogout}
              danger
              block
            >
              Sign Out
            </Button>
          )}
        </Space>
      </Drawer>
    </>
  )
}
